<template>
  <div class="settings-form-card">
    <div class="setting-row">
      <div class="setting-row-head">
        <div class="setting-row-label">MCP endpoint</div>
        <div class="setting-row-desc">Use this Streamable HTTP URL with a personal bearer token.</div>
      </div>
      <div class="setting-control token-copy-row">
        <input class="setting-input token-value" :value="endpoint" readonly aria-label="MCP endpoint" />
        <button type="button" class="btn" @click="copy(endpoint, 'Endpoint copied.')">Copy</button>
      </div>
    </div>

    <div class="setting-row">
      <div class="setting-row-head">
        <div class="setting-row-label">Create token</div>
        <div class="setting-row-desc">The secret is shown once. Store it in your MCP client immediately.</div>
      </div>
      <div class="setting-control">
        <label class="control-field">
          <span class="control-field-label">Name</span>
          <input v-model="name" class="setting-input" maxlength="100" placeholder="My MCP client" />
        </label>
        <label class="control-field">
          <span class="control-field-label">Expires after</span>
          <select v-model="lifetime" class="setting-input">
            <option :value="PersonalAccessTokenLifetime.THIRTY_DAYS">30 days</option>
            <option :value="PersonalAccessTokenLifetime.NINETY_DAYS">90 days</option>
            <option :value="PersonalAccessTokenLifetime.ONE_YEAR">1 year</option>
          </select>
        </label>
        <button type="button" class="btn btn-primary" :disabled="busy || !name.trim()" @click="createToken">
          {{ creating ? 'Creating…' : 'Create token' }}
        </button>
        <div v-if="createdToken" class="token-secret">
          <strong>Copy this token now. It will not be shown again.</strong>
          <div class="token-copy-row">
            <input class="setting-input token-value" :value="createdToken" readonly aria-label="New personal access token" />
            <button type="button" class="btn" @click="copy(createdToken, 'Token copied.')">Copy</button>
          </div>
        </div>
      </div>
    </div>

    <div class="setting-row">
      <div class="setting-row-head">
        <div class="setting-row-label">Personal tokens</div>
        <div class="setting-row-desc">Expired tokens remain listed until you revoke them.</div>
      </div>
      <div class="setting-control token-list">
        <span v-if="loading" class="token-empty">Loading…</span>
        <span v-else-if="tokens.length === 0" class="token-empty">No personal tokens.</span>
        <template v-else>
          <div v-for="token in tokens" :key="String(token.id)" class="token-item">
            <div class="token-details">
              <strong>{{ token.name }}</strong>
              <span :class="{ expired: isExpired(token) }">
                {{ isExpired(token) ? 'Expired' : 'Expires' }} {{ formatInstant(token.expiresAt) }}
              </span>
            </div>
            <button type="button" class="btn btn-danger" :disabled="busy" @click="confirmingToken = token">Revoke</button>
          </div>
        </template>
      </div>
    </div>

    <div v-if="message" class="token-message" role="status">{{ message }}</div>

    <ConfirmDialog
      v-if="confirmingToken"
      title="Revoke personal token"
      :message="`Revoke token &quot;${confirmingToken.name}&quot;? Connected MCP clients will immediately lose access.`"
      confirm-label="Revoke"
      danger
      @confirm="revokeToken"
      @cancel="confirmingToken = null"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import {
  CommandsService,
  PersonalAccessTokenLifetime,
  QueriesService,
  type PersonalAccessTokenResponse
} from '../../api/generated';
import { formatInstant } from '../../format';
import { getErrorMessage } from '../../stores/session';
import ConfirmDialog from '../ConfirmDialog.vue';

const endpoint = `${window.location.origin}/mcp`;
const name = ref('');
const lifetime = ref(PersonalAccessTokenLifetime.NINETY_DAYS);
const tokens = ref<PersonalAccessTokenResponse[]>([]);
const createdToken = ref<string | null>(null);
const confirmingToken = ref<PersonalAccessTokenResponse | null>(null);
const message = ref<string | null>(null);
const loading = ref(true);
const creating = ref(false);
const revoking = ref(false);
const busy = computed(() => creating.value || revoking.value);

onMounted(loadTokens);

async function loadTokens(): Promise<void> {
  loading.value = true;
  try {
    tokens.value = (await QueriesService.queryGetPersonalAccessTokensQuery({ requestBody: {} })).tokens ?? [];
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to load personal tokens.');
  } finally {
    loading.value = false;
  }
}

async function createToken(): Promise<void> {
  creating.value = true;
  message.value = null;
  createdToken.value = null;
  try {
    const result = await CommandsService.commandCreatePersonalAccessTokenCommand({
      requestBody: { name: name.value.trim(), lifetime: lifetime.value }
    });
    createdToken.value = result.token;
    name.value = '';
    await loadTokens();
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to create personal token.');
  } finally {
    creating.value = false;
  }
}

async function revokeToken(): Promise<void> {
  const token = confirmingToken.value;
  if (!token) {
    return;
  }

  confirmingToken.value = null;
  revoking.value = true;
  message.value = null;
  try {
    await CommandsService.commandRevokePersonalAccessTokenCommand({ requestBody: { id: token.id } });
    tokens.value = tokens.value.filter(candidate => candidate.id !== token.id);
    message.value = 'Token revoked.';
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to revoke personal token.');
  } finally {
    revoking.value = false;
  }
}

async function copy(value: string, successMessage: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(value);
    message.value = successMessage;
  } catch (error) {
    message.value = getErrorMessage(error, 'Unable to copy to clipboard.');
  }
}

function isExpired(token: PersonalAccessTokenResponse): boolean {
  return new Date(String(token.expiresAt)).getTime() <= Date.now();
}
</script>

<style scoped>
.token-copy-row {
  width: 100%;
  flex-direction: row;
  align-items: center;
}

.token-value {
  max-width: none;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}

.token-secret {
  display: grid;
  gap: var(--space-2);
  width: 100%;
  padding: var(--space-3);
  border: 1px solid var(--warning);
  border-radius: var(--radius-sm);
  background: color-mix(in srgb, var(--warning) 10%, transparent);
  font-size: 0.82rem;
}

.token-list {
  width: 100%;
}

.token-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  width: 100%;
  padding-bottom: var(--space-3);
  border-bottom: 1px solid var(--line);
}

.token-item:last-child {
  padding-bottom: 0;
  border-bottom: none;
}

.token-details {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.token-details span,
.token-empty {
  color: var(--text-muted);
  font-size: 0.78rem;
}

.token-details .expired {
  color: var(--danger);
}

.token-message {
  padding: var(--space-3) var(--space-5);
  border-top: 1px solid var(--line);
  color: var(--text-muted);
  font-size: 0.82rem;
}
</style>
